import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  AlertTriangle,
  Save,
  Check,
  Loader2,
  Upload,
  FileText,
  Coffee,
  Utensils,
  Plus,
  ShoppingBag,
  Wine,
  ChefHat
} from "lucide-react";

// Import JSON file
import menuJson from "./menu-agosto-2025.json";

const UploadMenuComponent = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [currentCategory, setCurrentCategory] = useState("");
  const [currentItem, setCurrentItem] = useState("");

  const categories = [
    { name: "entradas", displayName: "Entradas", icon: ChefHat, color: "text-orange-600" },
    { name: "fuertes", displayName: "Fuertes", icon: Utensils, color: "text-red-600" },
    { name: "extras", displayName: "Extras", icon: Plus, color: "text-yellow-600" },
    { name: "postres", displayName: "Postres", icon: Coffee, color: "text-pink-600" },
    { name: "merch", displayName: "Merch", icon: ShoppingBag, color: "text-purple-600" },
    { name: "bebidas", displayName: "Bebidas", icon: Wine, color: "text-blue-600" }
  ];

  const uploadDataToFirebase = async () => {
    if (!user) {
      setError("Usuario no autenticado");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setProgress(0);

    try {
      let totalItems = 0;
      let completedItems = 0;

      // Count total items first
      categories.forEach(category => {
        const categoryData = menuJson[category.name];
        if (categoryData && Array.isArray(categoryData)) {
          totalItems += categoryData.length;
        }
      });

      // Upload each category as separate collection
      for (const category of categories) {
        setCurrentCategory(category.displayName);

        const categoryData = menuJson[category.name];
        if (!categoryData || !Array.isArray(categoryData)) {
          console.warn(`No data found for category: ${category.name}`);
          continue;
        }

        // Create collection reference for this category
        const categoryCollection = collection(db, "Restaurants", "AlmacenGDL", category.name);

        // Upload each item as individual document
        for (let i = 0; i < categoryData.length; i++) {
          const item = categoryData[i];
          setCurrentItem(item.name);

          const itemDocument = {
            ...item,
            // Ensure consistent data types
            price: typeof item.price === 'string' ? parseFloat(item.price) : item.price,
            active: item.active !== undefined ? item.active : true,
            category: category.displayName,

            // Add category-specific fields
            ...(category.name === 'merch' && { stock: item.stock || 0 }),
            ...(category.name === 'bebidas' && item.weight && {
              size: item.weight,
              weight: undefined
            }),

            // Metadata
            uploadedBy: user.uid,
            timestamp: serverTimestamp(),
            version: "2025-08"
          };

          // Remove undefined fields
          Object.keys(itemDocument).forEach(key => {
            if (itemDocument[key] === undefined) {
              delete itemDocument[key];
            }
          });

          await addDoc(categoryCollection, itemDocument);

          completedItems++;
          setProgress((completedItems / totalItems) * 100);

          // Small delay to prevent overwhelming Firestore
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      setProgress(100);
      setSuccess(true);
      setCurrentCategory("");
      setCurrentItem("");

    } catch (err) {
      setError(`Error al subir datos: ${err.message}`);
      console.error("Upload error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/dashboard"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  // Count total items
  const totalItems = categories.reduce((total, cat) => {
    return total + (menuJson[cat.name]?.length || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6">
            <h2 className="text-3xl font-bold text-white flex items-center">
              <Upload className="mr-3 h-8 w-8" />
              Importador de Menú - Individual Items
            </h2>
            <p className="text-blue-100 mt-2">
              Cada producto será un documento individual en su colección respectiva
            </p>
          </div>

          <div className="p-8">
            {/* Menu Preview */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Vista Previa del Menú
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((category) => {
                  const items = menuJson[category.name] || [];
                  const IconComponent = category.icon;

                  return (
                    <div key={category.name} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                      <IconComponent className={`h-8 w-8 mx-auto mb-2 ${category.color}`} />
                      <h4 className="font-semibold text-gray-800 text-sm">{category.displayName}</h4>
                      <p className="text-gray-600 text-xs mt-1">{items.length} productos</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800 font-medium">
                  Total de productos: <span className="font-bold">{totalItems}</span>
                </p>
                <p className="text-blue-600 text-sm mt-1">
                  Se crearán {totalItems} documentos individuales en {categories.length} colecciones separadas
                </p>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start">
                <AlertTriangle className="mr-3 h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error al subir datos</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Success Display */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200 flex items-center">
                <Check className="mr-3 h-5 w-5" />
                <div>
                  <p className="font-medium">¡Datos subidos exitosamente!</p>
                  <p className="text-sm mt-1">Redirigiendo al dashboard en 3 segundos...</p>
                </div>
              </div>
            )}

            {/* Progress Section */}
            {loading && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-gray-700 font-medium">
                    {currentCategory ? `Subiendo: ${currentCategory}` : "Preparando datos..."}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {Math.round(progress)}%
                  </p>
                </div>
                {currentItem && (
                  <p className="text-sm text-gray-500 mb-2">
                    Procesando: {currentItem}
                  </p>
                )}
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Upload Structure Info */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-3">Estructura que se creará:</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="font-mono">📁 Restaurants/AlmacenGDL/</p>
                <div className="ml-6 space-y-1">
                  <p className="font-mono">├── 📁 beers/ (ya existe)</p>
                  {categories.map(cat => (
                    <p key={cat.name} className="font-mono">├── 📁 {cat.name}/ (documentos individuales)</p>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Cada producto será un documento separado con ID único generado por Firestore
                </p>
              </div>
            </div>

            {/* Upload Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={uploadDataToFirebase}
                disabled={loading || success}
                className={`flex-1 px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center ${loading || success
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 active:scale-95"
                  }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-3 h-5 w-5" />
                    Subiendo productos...
                  </>
                ) : success ? (
                  <>
                    <Check className="mr-3 h-5 w-5" />
                    ¡Completado!
                  </>
                ) : (
                  <>
                    <Save className="mr-3 h-5 w-5" />
                    Subir {totalItems} Productos
                  </>
                )}
              </button>

              {!loading && !success && (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-6 py-4 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                >
                  Cancelar
                </button>
              )}
            </div>

            {/* Additional Info */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-yellow-800 text-sm">
                  <p className="font-medium">Información importante:</p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Cada producto será un documento individual</li>
                    <li>Se crearán colecciones separadas: entradas, fuertes, extras, postres, merch, bebidas</li>
                    <li>Estructura idéntica a la colección "beers" existente</li>
                    <li>Campos normalizados y metadata incluida</li>
                    <li>Compatible con operaciones CRUD individuales</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadMenuComponent;